import { useEffect, useLayoutEffect, useRef } from 'react';
import { isRenderedForTab } from '@charitystack/shared';

/**
 * useStepFocus
 *
 * A11-003. Form steps stay mounted and an inactive one is hidden with
 * `visibility: hidden` (or `display: none` in the overlay stack). The Next or
 * Back button a donor pressed lives on the step that just hid, so the browser
 * drops focus to <body> and the next Tab starts from the top of the document.
 *
 * When a step becomes active and focus is on <body> or still inside a step that
 * just hid, this moves focus to the new step: its heading when it has one,
 * otherwise the step container itself. Neither is a text field, so a touch
 * donor does not get a soft keyboard pushed up by tapping Next. The target is
 * marked `data-cs-step-focus`, which forms.css uses to suppress the focus
 * outline on it — the move is for assistive tech and the Tab order, not a
 * visible ring jumping around the card.
 *
 * It does nothing on first mount, while focus is somewhere else on purpose, or
 * when this document does not have focus (an embed must never pull focus from
 * the host page on a programmatic step change).
 *
 * It also marks an inactive step `inert`, from a layout effect so the step that
 * is transitioning out is unfocusable before the next paint. Hiding a step with
 * `visibility` is not enough on its own: a descendant can set `visibility:
 * visible` again (react-select's input container does), which left a hidden
 * step's input in the Tab order.
 *
 * @param {boolean} isActive - whether this step is the visible one
 * @returns {import('react').RefObject<HTMLElement>} ref for the step's root
 */
export function useStepFocus(isActive) {
  const stepRef = useRef(null);
  const wasActiveRef = useRef(isActive);

  useLayoutEffect(() => {
    stepRef.current?.toggleAttribute('inert', !isActive);
  }, [isActive]);

  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = isActive;
    if (!isActive || wasActive) return;

    const step = stepRef.current;
    if (!step) return;
    const doc = step.ownerDocument;
    if (!doc.hasFocus()) return;

    const focused = doc.activeElement;
    // Judge a focused control by its OWN step (nearest `.page`), not by any
    // ancestor: the overlay's drawer copy of the form sits inside an inactive
    // outer page that its active step makes visible again, so "has an inactive
    // ancestor" called visible controls hidden. A control is dropped when its
    // own step is a sibling step that just went inactive. Its computed
    // visibility cannot decide this alone: a button with a transition still
    // reads visible for the length of it.
    const ownStep = focused?.closest?.('.page');
    const inHiddenSiblingStep =
      Boolean(ownStep) && !ownStep.classList.contains('active') && !ownStep.contains(step);
    const focusWasDropped =
      !focused ||
      focused === doc.body ||
      !focused.isConnected ||
      inHiddenSiblingStep ||
      !isRenderedForTab(focused);
    if (!focusWasDropped || step.contains(focused)) return;

    const heading = step.querySelector('h1, h2, h3, h4, h5, h6');
    const headingStep = heading?.closest('.page');
    const target =
      heading && (headingStep === step || headingStep?.classList.contains('active')) ? heading : step;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.setAttribute('data-cs-step-focus', '');
    target.focus({ preventScroll: true });
  }, [isActive]);

  return stepRef;
}
